import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { SpaceFacade } from '@simOn/space';
import { UserPreferencesFacade } from '@simOn/user';
import { PROJECT_INITIAL_VIEW } from '@simOn/user/preferences/model';
import { PublicLinkService } from '@simOn/public-link';
import { combineLatest, filter, map, take } from 'rxjs';

export const spaceInitialViewGuardFn: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const apartmentId = route.params['apartmentId'];

  // In-app navigation from within the space (e.g. dashboard -> 3D walk) should
  // reach the viewer normally, so the no-scan view can be shown there.
  const isNavigatingWithinSpace = router.url.startsWith(`/space/${apartmentId}/`);
  if (isNavigatingWithinSpace) {
    return true;
  }

  // "Set position" from an overlay (task/asset/event/ticket) must always land on
  // the 3D walk to place the position, regardless of the dashboard-first preference.
  const positionQueryParams = [
    'goToTaskPosition',
    'goToAssetPosition',
    'goToEventPosition',
    'editTaskPosition',
    'editAssetPosition',
    'editEventPosition',
    'editTicketPosition'
  ];
  const isSetPositionNavigation = positionQueryParams.some(
    (queryParam) => state.root.queryParamMap.get(queryParam) === 'true'
  );
  if (isSetPositionNavigation) {
    return true;
  }

  // Public link has no user profile (preferences are never loaded), so it always
  // opens the 3D walk - `startingScreen`/`interior` from the link only apply
  // there. Spaces without a scan show the public no-scan info there instead.
  if (inject(PublicLinkService).isPublicLinkModeActive()) {
    return true;
  }

  const scanType$ = inject(SpaceFacade).scanType$.pipe(filter((type) => type !== 'UNKNOWN'));
  const projectInitialView$ = inject(UserPreferencesFacade).preferences$.pipe(
    map(({ preferences }) => preferences.projectInitialView)
  );

  // Entering the space from outside (spaces list, deep link, page refresh).
  return combineLatest([scanType$, projectInitialView$]).pipe(
    take(1),
    map(([scanType, projectInitialView]) => {
      // Spaces without a scan always land on dashboard/assets, regardless of
      // the user's projectInitialView preference.
      if (scanType === 'NONE') {
        return router.createUrlTree(['space', apartmentId, 'dashboard', 'assets']);
      }

      // Only an explicit Dashboard preference leaves the 3D walk; ThreeDView and
      // Unknown (matching DEFAULT_USER_PREFERENCES) open the 3D walk.
      if (projectInitialView === PROJECT_INITIAL_VIEW.Dashboard) {
        return router.createUrlTree(['space', apartmentId, 'dashboard']);
      }

      return true;
    })
  );
};
